import { observer } from 'mobx-react-lite';
import { useCallback, useRef, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { useStore } from '@shared/hooks/useStore';
import {
    PDFViewer,
    PDFViewerRef,
    PluginRegistry,
    DocumentManagerCapability,
    ZoomCapability,
    SelectionCapability,
    UICapability,
    CommandsCapability,
    registerIcon,
    ZoomMode
} from '@embedpdf/react-pdf-viewer';
import { jaJP } from '../locales/ja';

interface PDFPageViewerProps {
    zoom?: number,
    onPageLoad?: (zoom: number) => void
}

// Register custom icons
registerIcon('custom-highlight', {
    viewBox: '0 0 512 512',
    path: 'M510.7,253.4L308.2,53.8c-1.8-1.8-4.6-1.8-6.4,0l0,0L165.3,188.3c-1.8,1.8-1.8,4.7-0.1,6.5l0.1,0.1l22.9,22.5l-67,65.9c-1.8,1.8-1.8,4.7-0.1,6.5l0.1,0.1l22.6,22.2L35.7,418.8H4.6c-2.5,0-4.6,2.1-4.6,4.6v31.5c0,2.5,2.1,4.6,4.6,4.6h197.1c1.2,0,2.3-0.5,3.2-1.3l43.5-43.2l23.1,22.7c1.8,1.8,4.6,1.8,6.4,0l0,0l66.9-66.1l22.9,22.6c1.8,1.8,4.6,1.8,6.4,0l0,0l136.4-134.4C512.5,258.1,512.5,255.2,510.7,253.4z M186.2,418.5H94.6l76.8-76l45.8,45.1L186.2,418.5z M274.7,383l-98-96.5l39.2-38.6l98,96.5L274.7,383z M371,339.5l-150.2-148l84.2-82.9l150.2,148L371,339.5z',
    fill: '#fff'
});

registerIcon('custom-note', {
    viewBox: '0 0 363 340',
    path: 'M323.97 3.48806C321.837 5.6214 318.504 10.0214 316.504 13.4881L313.037 19.7547L316.104 21.6214C317.837 22.6881 326.504 28.1547 335.437 33.6214L351.704 43.6214L356.504 36.1547C359.17 32.0214 361.57 27.0881 362.104 25.3547C363.57 19.4881 359.57 14.2881 347.437 6.8214C334.104 -1.31194 329.704 -1.84527 323.97 3.48806Z M306.77 29.6213C306.104 30.5546 298.637 42.2879 290.237 55.8879C281.837 69.4879 267.304 92.8213 257.97 107.755C248.637 122.821 240.104 136.821 238.904 139.088C235.304 146.155 225.304 191.888 226.637 195.488C229.037 201.888 233.97 199.621 253.97 183.355C264.104 174.955 273.837 166.688 275.437 164.955C277.037 163.221 293.704 137.221 312.37 107.221C341.037 61.2213 346.104 52.2879 344.37 51.2213C315.037 32.4213 307.837 28.2879 306.77 29.6213ZM258.37 151.488C262.904 154.288 266.504 156.821 266.504 157.221C266.504 158.688 250.77 170.555 248.77 170.555C244.237 170.555 243.437 167.088 245.97 156.555C247.304 151.088 248.77 146.555 249.304 146.555C249.837 146.555 253.97 148.821 258.37 151.488Z M22.2378 60.4213C10.9045 64.4213 5.03779 71.2213 1.83779 83.488C-0.0288806 90.5547 -0.162214 103.088 0.104453 177.488C0.504453 257.755 0.637786 263.621 3.03779 268.955C6.23779 276.155 15.5711 284.421 22.7711 286.421C25.9711 287.221 39.0378 288.155 51.8378 288.421C70.7711 288.821 76.5045 289.355 82.5045 291.488C86.7711 292.955 102.371 302.555 118.504 313.355C156.771 339.088 158.104 339.888 164.904 339.888C172.371 339.888 173.304 339.355 210.371 314.555C228.104 302.688 244.771 292.421 248.504 291.221C253.438 289.621 261.571 288.821 279.038 288.421C292.238 288.155 305.171 287.221 307.704 286.421C315.704 283.888 321.971 278.821 325.971 271.488L329.838 264.555L330.238 185.221L330.638 105.888L326.104 113.221C323.704 117.221 320.104 123.088 318.104 126.155L314.638 131.621L314.238 195.488C313.838 253.355 313.571 259.621 311.438 263.488C307.571 270.555 305.438 271.088 277.171 272.021C242.238 272.955 242.504 272.955 200.638 300.955C182.371 313.221 166.904 323.488 166.238 323.755C165.571 323.888 149.571 314.021 130.771 301.488C111.704 288.955 92.9045 277.488 88.5045 275.888C81.5711 273.221 77.0378 272.688 53.8378 272.021C24.9045 271.088 21.9711 270.421 18.6378 263.088C16.3711 258.021 16.5045 88.9547 18.9045 84.1547C19.7045 82.288 22.2378 79.488 24.2378 78.0213C27.9711 75.2213 28.7711 75.2213 144.638 74.8213L261.171 74.5547L265.838 67.2213C268.371 63.088 270.504 59.488 270.504 59.2213C270.504 58.8213 215.704 58.5547 148.904 58.5547C50.9045 58.688 26.2378 58.9547 22.2378 60.4213Z M56.7714 116.955C53.9714 120.021 53.838 125.755 56.638 128.421C58.5047 130.288 68.1047 130.555 142.505 130.555H226.238L228.105 127.488C229.171 125.888 231.305 122.288 233.038 119.488L236.105 114.555H147.438C63.438 114.555 58.7714 114.688 56.7714 116.955Z M57.4379 167.355C54.1046 169.621 53.3046 176.821 56.1046 179.621C57.3046 180.821 76.7712 181.221 135.305 181.221H212.905L213.838 176.155C214.238 173.488 215.038 169.888 215.438 168.155L216.105 165.221H138.238C69.9712 165.221 60.1046 165.488 57.4379 167.355Z M56.6386 219.355C53.4386 222.421 53.9719 228.688 57.4386 231.088C60.1052 232.955 72.5052 233.221 165.572 233.221H270.639L273.839 229.888C277.705 226.155 277.972 224.288 275.039 220.155L273.039 217.221H165.839C70.2386 217.221 58.5052 217.488 56.6386 219.355Z',
    fill: '#fff'
});

interface SearchHighlightRect {
    pageIndex: number,
    rects: Array<{ x: number, y: number, width: number, height: number }>,
    word: string
}

interface NoteHighlightRect {
    id: string,
    pageIndex: number,
    rects: Array<{ x: number, y: number, width: number, height: number }>,
    color?: string
}

const PDFPageViewer = observer(({ zoom, onPageLoad }: PDFPageViewerProps) => {
    const { readerStore } = useStore();
    const { currentPageUrl, isLoadingPage } = readerStore;
    const activeBook = readerStore.getActiveBook();

    const pageContainerRef = useRef<HTMLDivElement>(null);
    const viewerRef = useRef<PDFViewerRef>(null);
    const registryRef = useRef<PluginRegistry | null>(null);

    const [viewerKey, setViewerKey] = useState(0);
    const [isViewerReady, setIsViewerReady] = useState(false);
    const [documentError, setDocumentError] = useState<string | null>(null);
    const [searchHighlights, setSearchHighlights] = useState<SearchHighlightRect[]>([]);
    const [noteHighlights, setNoteHighlights] = useState<NoteHighlightRect[]>([]);
    const [currentScale, setCurrentScale] = useState(1);

    const searchHighlightTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isInitialZoomSetRef = useRef(false);
    const selectionObserverRef = useRef<MutationObserver | null>(null);
    const zoomUnsubscribeRef = useRef<(() => void) | null>(null);

    const getCleanSelectedText = async (
        selectionCapability: SelectionCapability,
        registry: PluginRegistry,
        docManager: DocumentManagerCapability
    ): Promise<string> => {
        const documentId = docManager.getActiveDocumentId();
        if (!documentId) return '';

        const selection = selectionCapability.forDocument(documentId);
        const engine = registry.getEngine();
        const docObj = docManager.getActiveDocument();
        const state = selectionCapability.getState(documentId);

        if (!engine || !docObj || !state || !state.selection) {
            const lines = await selection.getSelectedText().toPromise();
            return lines?.join(' ').trim() || '';
        }

        const sel = state.selection;

        let hasGhost = false;
        for (let p = sel.start.page; p <= sel.end.page && !hasGhost; p++) {
            const geo = state.geometry[p];
            if (!geo || !geo.runs) continue;

            const pageData = docObj.pages?.[p];
            const pageWidth = pageData?.width ?? pageData?.size?.width ?? 0;

            for (const run of geo.runs) {
                const runEnd = run.charStart + (run.glyphs?.length || 0);
                const overlapStart = Math.max(sel.start.index, run.charStart);
                const overlapEnd = Math.min(sel.end.index + 1, runEnd);

                if (overlapEnd > overlapStart) {
                    const posX = run.rect?.origin?.x ?? run.rect?.x ?? 0;
                    if (posX < 0 || (pageWidth > 0 && posX > pageWidth)) {
                        hasGhost = true;
                        break;
                    }
                }
            }
        }

        if (!hasGhost) {
            const lines = await selection.getSelectedText().toPromise();
            return lines?.join(' ').trim() || '';
        }

        const cleanSlices: any[] = [];

        for (let p = sel.start.page; p <= sel.end.page; p++) {
            const geo = state.geometry[p];
            if (!geo || !geo.runs) continue;

            const pageData = docObj.pages?.[p];
            const pageWidth = pageData?.width ?? pageData?.size?.width ?? 0;
            let currentSlice: any = null;

            geo.runs.forEach((run: any) => {
                const runEnd = run.charStart + (run.glyphs?.length || 0);
                const overlapStart = Math.max(sel.start.index, run.charStart);
                const overlapEnd = Math.min(sel.end.index + 1, runEnd);

                if (overlapEnd > overlapStart) {
                    const posX = run.rect?.origin?.x ?? run.rect?.x ?? 0;
                    const isGhost = posX < 0 || (pageWidth > 0 && posX > pageWidth);

                    if (!isGhost) {
                        if (currentSlice && currentSlice.charIndex + currentSlice.charCount === overlapStart) {
                            currentSlice.charCount += (overlapEnd - overlapStart);
                        } else {
                            currentSlice = {
                                pageIndex: p,
                                charIndex: overlapStart,
                                charCount: overlapEnd - overlapStart
                            };
                            cleanSlices.push(currentSlice);
                        }
                    } else {
                        currentSlice = null;
                    }
                }
            });
        }

        if (cleanSlices.length > 0) {
            const textArray = await engine.getTextSlices(docObj, cleanSlices).toPromise();
            const mergedText = textArray.join(' ').trim();
            return mergedText.replace(/[\u00AD\u200B\u2060\uFEFF\uFFFE\uFFFF]/g, '');
        }

        return '';
    };

    const getFullPageText = useCallback(async (): Promise<string> => {
        if (!registryRef.current) return '';

        const engine = registryRef.current.getEngine();
        const docManagerPlugin = registryRef.current.getCapabilityProvider('document-manager');
        const docManager = docManagerPlugin?.provides?.() as DocumentManagerCapability;
        const docObj = (docManager as any)?.getActiveDocument();

        if (!engine || !docObj || !docObj.pages || docObj.pages.length === 0) return '';

        try {
            const pageIndex = 0;
            const page = docObj.pages[pageIndex];
            if (!page) return '';

            const pageWidth = page?.width ?? page?.size?.width ?? 0;

            const geometry = await engine.getPageGeometry(docObj, page).toPromise();
            if (!geometry || !geometry.runs || geometry.runs.length === 0) {
                const charCount = page.charCount ?? page.textLength ?? 10000;
                const slices = [{ pageIndex, charIndex: 0, charCount }];
                const textArray = await engine.getTextSlices(docObj, slices).toPromise();
                return (textArray?.join(' ').trim() || '').replace(/[\u00AD\u200B\u2060\uFEFF\uFFFE\uFFFF]/g, '');
            }

            const cleanSlices: any[] = [];
            let currentSlice: any = null;

            geometry.runs.forEach((run: any) => {
                const posX = run.rect?.origin?.x ?? run.rect?.x ?? 0;
                const isGhost = posX < 0 || (pageWidth > 0 && posX > pageWidth);

                if (!isGhost) {
                    const charStart = run.charStart ?? 0;
                    const charCount = run.glyphs?.length || 0;

                    if (currentSlice && currentSlice.charIndex + currentSlice.charCount === charStart) {
                        currentSlice.charCount += charCount;
                    } else {
                        currentSlice = {
                            pageIndex,
                            charIndex: charStart,
                            charCount
                        };
                        cleanSlices.push(currentSlice);
                    }
                } else {
                    currentSlice = null;
                }
            });

            if (cleanSlices.length > 0) {
                const textArray = await engine.getTextSlices(docObj, cleanSlices).toPromise();
                const fullText = textArray?.join(' ').trim() || '';
                return fullText.replace(/[\u00AD\u200B\u2060\uFEFF\uFFFE\uFFFF]/g, '');
            }

            return '';
        } catch (error) {
            console.error('Failed to get full page text:', error);
            return '';
        }
    }, []);

    const injectCustomStylesPDFShadowDOM = () => {
        try {
            if (pageContainerRef.current) {
                const embedContainer = pageContainerRef.current.querySelector('embedpdf-container');
                if (embedContainer && embedContainer.shadowRoot) {
                    const shadowRoot = embedContainer.shadowRoot;

                    if (selectionObserverRef.current) {
                        selectionObserverRef.current.disconnect();
                    }

                    const style = document.createElement('style');
                    style.textContent = `
                        #document-content {
                            outline: none !important;
                        }
                        #document-content [style*="mix-blend-mode"] {
                            outline: none !important;
                            max-width: none !important; 
                        }
                        #document-content [data-epdf-i="selection"] {
                            pointer-events: none !important;
                            max-width: none !important;
                            z-index: 1001 !important;
                        }
                        #document-content [data-epdf-i="selection"] > div {
                            display: flex !important;
                            background: #29292B !important;
                            background-color: #29292B !important;
                            background-image: none !important;
                            opacity: 1 !important;
                            pointer-events: auto !important;
                        }
                        #document-content [data-epdf-i="selection"] > div button:hover {
                            background: #29292B !important;
                            background-color: #29292B !important;
                            background-image: none !important;
                        }
                        #document-content .bg-bg-surface {
                            display: none !important;
                        }
                    `;
                    shadowRoot.appendChild(style);

                    const observer = new MutationObserver(() => {
                        const selectionDiv = shadowRoot.querySelector('[data-epdf-i="selection"] > div') as HTMLElement;
                        if (selectionDiv) {
                            const hasButton = selectionDiv.querySelector('button');
                            if (!hasButton) {
                                selectionDiv.style.setProperty('display', 'none', 'important');
                                selectionDiv.style.setProperty('opacity', '0', 'important');
                            } else {
                                selectionDiv.style.setProperty('display', 'flex', 'important');
                                selectionDiv.style.setProperty('opacity', '1', 'important');
                            }

                            if (selectionDiv.style.top) {
                                const topValue = parseFloat(selectionDiv.style.top);
                                if (!isNaN(topValue) && topValue < -16) {
                                    selectionDiv.style.top = '-16px';
                                }
                            }
                        }

                        const cleanupOverflow = (containers: NodeListOf<Element>) => {
                            containers.forEach((container: any) => {
                                const parentPage = container.parentElement;
                                if (parentPage) {
                                    const pageWidth = parseFloat(parentPage.style.width) || parentPage.offsetWidth;
                                    const pageHeight = parseFloat(parentPage.style.height) || parentPage.offsetHeight;
                                    const containerLeft = parseFloat(container.style.left || '0');
                                    const containerTop = parseFloat(container.style.top || '0');

                                    const childDivs = container.querySelectorAll('div');
                                    childDivs.forEach((div: HTMLElement) => {
                                        let shouldRemove = false;
                                        if (div.style.left && pageWidth > 0) {
                                            const left = parseFloat(div.style.left);
                                            const absoluteLeft = left + containerLeft;
                                            if (!isNaN(left) && (absoluteLeft >= pageWidth - 1 || absoluteLeft < -1)) {
                                                shouldRemove = true;
                                            }
                                        }
                                        if (!shouldRemove && div.style.top && pageHeight > 0) {
                                            const top = parseFloat(div.style.top);
                                            const absoluteTop = top + containerTop;
                                            if (!isNaN(top) && (absoluteTop >= pageHeight - 1 || absoluteTop < -1)) {
                                                shouldRemove = true;
                                            }
                                        }
                                        if (shouldRemove) {
                                            div.remove();
                                        }
                                    });
                                }
                            });
                        };

                        cleanupOverflow(shadowRoot.querySelectorAll('[style*="mix-blend-mode"]'));
                        cleanupOverflow(shadowRoot.querySelectorAll('#jiho-highlights-container'));
                    });

                    observer.observe(shadowRoot, {
                        attributes: true,
                        childList: true,
                        subtree: true,
                        attributeFilter: ['style']
                    });
                    selectionObserverRef.current = observer;
                }
            }
        } catch (styleError) {
            console.error('Failed to inject custom styles:', styleError);
        }
    };

    useEffect(() => {
        return () => {
            if (selectionObserverRef.current) {
                selectionObserverRef.current.disconnect();
            }
        };
    }, []);

    useEffect(() => {
        readerStore.setGetPageTextCallback(getFullPageText);
        return () => {
            readerStore.setGetPageTextCallback(null);
        };
    }, [getFullPageText, readerStore]);

    useEffect(() => {
        const handleError = (e: ErrorEvent) => {
            if (e.message === 'ResizeObserver loop completed with undelivered notifications.' ||
                e.message === 'ResizeObserver loop limit exceeded') {
                const resizeObserverErrDiv = document.getElementById(
                    'webpack-dev-server-client-overlay-div'
                );
                const resizeObserverErr = document.getElementById(
                    'webpack-dev-server-client-overlay'
                );
                if (resizeObserverErr) {
                    resizeObserverErr.setAttribute('style', 'display: none');
                }
                if (resizeObserverErrDiv) {
                    resizeObserverErrDiv.setAttribute('style', 'display: none');
                }
                e.stopImmediatePropagation();
            }
        };
        window.addEventListener('error', handleError);
        return () => window.removeEventListener('error', handleError);
    }, []);

    useEffect(() => {
        if (currentPageUrl) {
            if (zoomUnsubscribeRef.current) {
                zoomUnsubscribeRef.current();
                zoomUnsubscribeRef.current = null;
            }

            setViewerKey(prev => prev + 1);
            setIsViewerReady(false);
            setDocumentError(null);
            registryRef.current = null;
            setSearchHighlights([]);
            setNoteHighlights([]);
            isInitialZoomSetRef.current = false;
        }
    }, [currentPageUrl]);

    useEffect(() => {
        if (!zoom) return;

        let isCancelled = false;
        let attempts = 0;

        const updateZoom = async () => {
            if (!registryRef.current || isCancelled) return;

            try {
                const docManagerPlugin = registryRef.current.getCapabilityProvider('document-manager');
                const docManager = docManagerPlugin?.provides?.() as DocumentManagerCapability;
                const activeDocumentId = docManager?.getActiveDocumentId();

                const zoomPlugin = registryRef.current.getCapabilityProvider('zoom');
                const zoomCapability = zoomPlugin?.provides?.() as ZoomCapability;

                if (activeDocumentId && zoomCapability) {
                    const zoomScope = zoomCapability.forDocument(activeDocumentId);
                    zoomScope.requestZoom(zoom / 100);

                    const retryDelays = [100, 300, 500];
                    retryDelays.forEach((delay) => {
                        setTimeout(() => {
                            if (!isCancelled) {
                                zoomScope.requestZoom(zoom / 100);
                            }
                        }, delay);
                    });
                } else if (attempts < 50) {
                    attempts++;
                    setTimeout(updateZoom, 50);
                }
            } catch (error) {
                console.error('Failed to update zoom:', error);
            }
        };

        updateZoom();

        return () => {
            isCancelled = true;
        };
    }, [zoom]);

    // Apply note highlights
    const applyNoteHighlights = useCallback(async () => {
        if (!isViewerReady || !registryRef.current) return;

        try {
            const currentNotes = readerStore.currentPageNotes;

            if (currentNotes.length === 0) {
                setNoteHighlights([]);
                return;
            }

            const newNoteHighlights: NoteHighlightRect[] = [];

            for (const note of currentNotes) {
                if (!note.highlight) continue;

                let match: any = null;

                if (note.position) {
                    try {
                        const pos = JSON.parse(note.position);
                        if (pos && typeof pos.pageIndex === 'number' && (pos.rect || pos.segmentRects || pos.rects)) {
                            match = pos;
                        }
                    } catch (e) {
                        console.error('Failed to parse note position:', e);
                    }
                }

                if (match) {
                    const rects = match.segmentRects || match.rects || (match.rect ? [match.rect] : []);
                    newNoteHighlights.push({
                        id: note.id,
                        pageIndex: match.pageIndex,
                        rects: rects.map((r: any) => ({
                            x: r.origin?.x ?? r.x ?? 0,
                            y: r.origin?.y ?? r.y ?? 0,
                            width: r.size?.width ?? r.width ?? 0,
                            height: r.size?.height ?? r.height ?? 0
                        })),
                        color: '#EDE074' // Default yellow highlight color
                    });
                }
            }

            setNoteHighlights(newNoteHighlights);
        } catch (error) {
            console.error('Failed to apply note highlights:', error);
            setNoteHighlights([]);
        }
    }, [isViewerReady, readerStore.currentPageNotes]);

    const applySearchHighlights = useCallback(async () => {
        if (!isViewerReady || !registryRef.current) return;

        if (readerStore.activeSearchHighlightWords.length === 0) {
            setSearchHighlights([]);
            return;
        }

        const engine = registryRef.current.getEngine();
        const docManagerPlugin = registryRef.current.getCapabilityProvider('document-manager');
        const docManager = docManagerPlugin?.provides?.() as DocumentManagerCapability;
        const doc = docManager?.getActiveDocument();
        const activeDocumentId = docManager?.getActiveDocumentId();

        const zoomPlugin = registryRef.current.getCapabilityProvider('zoom');
        const zoomCapability = zoomPlugin?.provides?.() as ZoomCapability;
        if (activeDocumentId && zoomCapability) {
            const zoomScope = zoomCapability.forDocument(activeDocumentId);
            const zoomState = zoomScope.getState();
            if (zoomState?.currentZoomLevel) {
                setCurrentScale(zoomState.currentZoomLevel);
            }
        }

        const highlightWords = readerStore.activeSearchHighlightWords;

        try {
            if (highlightWords.length > 0 && engine && doc && doc.pages && doc.pages.length > 0) {
                const pageIndex = 0;
                const pageData = doc.pages[pageIndex];
                const pageWidth = pageData.width ?? pageData.size?.width ?? 0;
                const pageHeight = pageData.height ?? pageData.size?.height ?? 0;

                const [geometry, allPageGlyphs] = await Promise.all([
                    engine.getPageGeometry(doc, pageData).toPromise(),
                    engine.getPageGlyphs?.(doc, pageData)?.toPromise().catch(() => null)
                ]);

                if (!geometry || !geometry.runs) {
                    setSearchHighlights([]);
                    return;
                }

                const cleanRuns = geometry.runs.filter((run: any) => {
                    const posX = run.rect?.origin?.x ?? run.rect?.x ?? 0;
                    const posY = run.rect?.origin?.y ?? run.rect?.y ?? 0;
                    return !(posX < 0 || (pageWidth > 0 && posX > pageWidth) || posY < 0 || (pageHeight > 0 && posY > pageHeight));
                });

                if (cleanRuns.length === 0) {
                    setSearchHighlights([]);
                    return;
                }

                const slices = cleanRuns.map((r: any) => ({
                    pageIndex,
                    charIndex: r.charStart,
                    charCount: r.glyphs?.length || 0
                }));

                const textArray = await engine.getTextSlices(doc, slices).toPromise();

                let searchableContent = '';
                const charMap: number[] = [];
                const innerMap: number[] = [];

                textArray.forEach((text: string, runIdx: number) => {
                    if (searchableContent.length > 0) {
                        searchableContent += ' ';
                        charMap.push(-1);
                        innerMap.push(-1);
                    }
                    for (let i = 0; i < text.length; i++) {
                        charMap.push(runIdx);
                        innerMap.push(i);
                    }
                    searchableContent += text;
                });

                const seenMatches = new Map<string, SearchHighlightRect>();
                const uniqueWords = Array.from(new Set(highlightWords));

                for (const word of uniqueWords) {
                    if (!word || word.trim().length === 0) continue;

                    let wordMatches: SearchHighlightRect[] = [];

                    try {
                        const searchTask = engine.searchAllPages(doc, word);
                        if (searchTask) {
                            const searchResult = await searchTask.toPromise();
                            if (searchResult && searchResult.results) {
                                const pageResults = searchResult.results.filter(r => r.pageIndex === pageIndex);
                                for (const res of pageResults) {
                                    const matchKey = `engine-${res.charIndex}-${res.charCount}`;
                                    if (seenMatches.has(matchKey)) continue;

                                    const targetCharIndex = res.charIndex;
                                    const targetCharCount = res.charCount;
                                    const targetEnd = targetCharIndex + targetCharCount;
                                    const rects: any[] = [];

                                    cleanRuns.forEach((run: any, _: number) => {
                                        const runStart = run.charStart;
                                        const runEnd = runStart + (run.glyphs?.length || 0);

                                        const overlapStart = Math.max(targetCharIndex, runStart);
                                        const overlapEnd = Math.min(targetEnd, runEnd);

                                        if (overlapEnd > overlapStart) {
                                            const rx = run.rect?.origin?.x ?? run.rect?.x ?? 0;
                                            const ry = run.rect?.origin?.y ?? run.rect?.y ?? 0;

                                            const glyphStartIdx = overlapStart - runStart;
                                            const glyphEndIdx = overlapEnd - runStart - 1;

                                            let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
                                            let foundAny = false;

                                            for (let gIdx = glyphStartIdx; gIdx <= glyphEndIdx && gIdx < run.glyphs.length; gIdx++) {
                                                let glyph = run.glyphs[gIdx];
                                                if (typeof glyph === 'number' && allPageGlyphs && (allPageGlyphs as any)[glyph]) {
                                                    glyph = (allPageGlyphs as any)[glyph];
                                                }
                                                if (glyph) {
                                                    const gx = glyph.origin?.x ?? glyph.x;
                                                    const gy = glyph.origin?.y ?? glyph.y;
                                                    const gw = glyph.size?.width ?? glyph.width ?? (typeof glyph.maxX === 'number' && typeof glyph.minX === 'number' ? glyph.maxX - glyph.minX : undefined);
                                                    const gh = glyph.size?.height ?? glyph.height ?? (typeof glyph.maxY === 'number' && typeof glyph.minY === 'number' ? glyph.maxY - glyph.minY : undefined);

                                                    if (typeof gx === 'number' && typeof gy === 'number' && typeof gw === 'number' && typeof gh === 'number') {
                                                        if (gx === 0 && gy === 0 && (rx > 1 || ry > 1)) {
                                                            continue;
                                                        }

                                                        minX = Math.min(minX, gx);
                                                        minY = Math.min(minY, gy);
                                                        maxX = Math.max(maxX, gx + gw);
                                                        maxY = Math.max(maxY, gy + gh);
                                                        foundAny = true;
                                                    }
                                                }
                                            }

                                            if (foundAny) {
                                                rects.push({ x: minX, y: minY, width: maxX - minX, height: maxY - minY });
                                            } else if (run.rect) {
                                                const r = run.rect;
                                                const rw = r.size?.width ?? r.width ?? 0;
                                                const rh = r.size?.height ?? r.height ?? 0;
                                                const rx = r.origin?.x ?? r.x ?? 0;
                                                const ry = r.origin?.y ?? r.y ?? 0;

                                                const ratioStart = (overlapStart - runStart) / (run.glyphs?.length || 1);
                                                const ratioEnd = (overlapEnd - runStart) / (run.glyphs?.length || 1);

                                                const calculated = {
                                                    x: rx + rw * ratioStart,
                                                    y: ry,
                                                    width: rw * (ratioEnd - ratioStart),
                                                    height: rh
                                                };
                                                rects.push(calculated);
                                            }
                                        }
                                    });

                                    if (rects.length > 0) {
                                        seenMatches.set(matchKey, { pageIndex, rects, word });
                                        wordMatches.push({ pageIndex, rects, word });
                                    } else if (res.rects && res.rects.length > 0) {
                                        const engineRects = res.rects.map((r: any) => ({
                                            x: r.origin?.x ?? r.x ?? 0,
                                            y: r.origin?.y ?? r.y ?? 0,
                                            width: r.size?.width ?? r.width ?? 0,
                                            height: r.size?.height ?? r.height ?? 0
                                        }));
                                        seenMatches.set(matchKey, { pageIndex, rects: engineRects, word });
                                        wordMatches.push({ pageIndex, rects: engineRects, word });
                                    }
                                }
                            }
                        }
                    } catch (err) {
                        console.warn('searchAllPages failed:', err);
                    }

                    if (wordMatches.length > 0) {
                        wordMatches.forEach((m, idx) => {
                            seenMatches.set(`${word}-engine-${idx}`, m);
                        });
                    }
                }

                const allMatches = Array.from(seenMatches.values());
                const allRects: { x: number, y: number, width: number, height: number }[] = [];
                allMatches.forEach((m) => {
                    m.rects.forEach((r) => {
                        allRects.push({ ...r });
                    });
                });

                if (allRects.length === 0) {
                    setSearchHighlights([]);
                    return;
                }

                const mergedRects: { x: number, y: number, width: number, height: number }[] = [];
                const sortedRects = allRects.sort((a, b) => (a.y - b.y) || (a.x - b.x));

                while (sortedRects.length > 0) {
                    let current = sortedRects.shift()!;
                    while (sortedRects.length > 0) {
                        const next = sortedRects[0];
                        const verticalOverlap = Math.abs(current.y - next.y) < 2;
                        const horizontalLink = (next.x >= current.x && next.x <= current.x + current.width + 10) || (current.x >= next.x && current.x <= next.x + next.width + 10);

                        if (verticalOverlap && horizontalLink) {
                            const minX = Math.min(current.x, next.x);
                            const minY = Math.min(current.y, next.y);
                            const maxX = Math.max(current.x + current.width, next.x + next.width);
                            const maxY = Math.max(current.y + current.height, next.y + next.height);
                            current = { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
                            sortedRects.shift();
                        } else {
                            break;
                        }
                    }
                    mergedRects.push(current);
                }
                setSearchHighlights([{
                    pageIndex,
                    rects: mergedRects,
                    word: uniqueWords.join(', ')
                }]);
            }
        } catch (error) {
            setSearchHighlights([]);
        }
    }, [isViewerReady, readerStore]);

    const renderHighlightsToShadowDOM = useCallback(() => {
        if (!pageContainerRef.current) return;

        const embedContainer = pageContainerRef.current.querySelector('embedpdf-container');
        if (!embedContainer || !embedContainer.shadowRoot) return;

        const shadowRoot = embedContainer.shadowRoot;

        const existingContainer = shadowRoot.querySelector('#jiho-highlights-container');
        if (existingContainer) existingContainer.remove();

        if (searchHighlights.length === 0 && noteHighlights.length === 0) return;

        let pageContainer = shadowRoot.querySelector('[data-epdf-i="page"]') as HTMLElement;

        if (!pageContainer) {
            const contentEl = shadowRoot.querySelector('img, canvas');
            if (contentEl) {
                let current = contentEl.parentElement;
                while (current && current !== shadowRoot.host) {
                    const style = getComputedStyle(current);
                    if (style.position === 'relative' || style.position === 'absolute') {
                        pageContainer = current as HTMLElement;
                        break;
                    }
                    current = current.parentElement;
                }
            }
        }

        if (!pageContainer) return;

        if (getComputedStyle(pageContainer).position === 'static') {
            pageContainer.style.position = 'relative';
        }

        const highlightsContainer = document.createElement('div');
        highlightsContainer.id = 'jiho-highlights-container';
        Object.assign(highlightsContainer.style, {
            position: 'absolute',
            left: '0',
            top: '0',
            width: '100%',
            height: '100%',
            pointerEvents: 'none'
        });

        const createHighlight = (rect: any, color: string, opacity: string, zIndex: string, attr: string, idx: string) => {
            const highlightDiv = document.createElement('div');
            highlightDiv.setAttribute(attr, 'true');
            highlightDiv.setAttribute('data-idx', idx);

            Object.assign(highlightDiv.style, {
                position: 'absolute',
                left: `${rect.x * currentScale}px`,
                top: `${rect.y * currentScale}px`,
                width: `${rect.width * currentScale}px`,
                height: `${rect.height * currentScale}px`,
                backgroundColor: color,
                mixBlendMode: 'multiply',
                opacity: opacity,
                pointerEvents: 'none',
                transform: 'scale(1.02)',
                transformOrigin: 'center',
                transition: 'opacity 0.3s ease-in-out',
                zIndex: zIndex
            });
            return highlightDiv;
        };

        searchHighlights.forEach((highlight, highlightIdx) => {
            highlight.rects.forEach((rect, rectIdx) => {
                const div = createHighlight(rect, '#FFBF00', '1', '10', 'data-search-highlight', `${highlightIdx}-${rectIdx}`);
                highlightsContainer.appendChild(div);
            });
        });

        noteHighlights.forEach((highlight, highlightIdx) => {
            highlight.rects.forEach((rect, rectIdx) => {
                const div = createHighlight(rect, highlight.color || '#EDE074', '0.5', '1', 'data-note-highlight', `${highlightIdx}-${rectIdx}`);
                highlightsContainer.appendChild(div);
            });
        });

        pageContainer.appendChild(highlightsContainer);
    }, [searchHighlights, noteHighlights, currentScale]);

    useEffect(() => {
        if (isViewerReady && (searchHighlights.length >= 0 || noteHighlights.length >= 0)) {
            renderHighlightsToShadowDOM();
        }
    }, [isViewerReady, searchHighlights, noteHighlights, currentScale, renderHighlightsToShadowDOM]);

    useEffect(() => {
        if (isViewerReady) {
            applyNoteHighlights();
        }
    }, [applyNoteHighlights, isViewerReady]);

    useEffect(() => {
        if (isViewerReady) {
            if (searchHighlightTimeoutRef.current) {
                clearTimeout(searchHighlightTimeoutRef.current);
            }
            searchHighlightTimeoutRef.current = setTimeout(() => {
                applySearchHighlights();
            }, 100);
        }
        return () => {
            if (searchHighlightTimeoutRef.current) {
                clearTimeout(searchHighlightTimeoutRef.current);
            }
        };
    }, [applySearchHighlights, isViewerReady, readerStore.activeSearchResultId, readerStore.searchResults.length]);

    useEffect(() => {
        if (!readerStore.isNoteModalOpen && isViewerReady && registryRef.current) {
            const selectionPlugin = registryRef.current.getCapabilityProvider('selection');
            const selectionCapability = selectionPlugin?.provides?.() as SelectionCapability;
            if (selectionCapability) {
                selectionCapability?.clear();
            }
        }
    }, [readerStore.isNoteModalOpen, isViewerReady]);

    const handleViewerReady = useCallback(async (registry: PluginRegistry) => {
        registryRef.current = registry;

        injectCustomStylesPDFShadowDOM();

        const docManagerPlugin = registry.getCapabilityProvider('document-manager');
        const docManager = docManagerPlugin?.provides?.() as DocumentManagerCapability;
        const activeDocumentId = docManager?.getActiveDocumentId();

        if (docManager) {
            docManager.onDocumentError?.((event) => {
                console.error('Document error:', event);
                setDocumentError(event.error?.message || 'ドキュメントの読み込みに失敗しました');
            });
        }

        const zoomPlugin = registry.getCapabilityProvider('zoom');
        const zoomCapability = zoomPlugin?.provides?.() as ZoomCapability;

        if (activeDocumentId && zoomCapability) {
            const zoomScope = zoomCapability.forDocument(activeDocumentId);

            const zoomUnsubscribe = zoomScope.onStateChange((newState) => {
                if (newState && newState.currentZoomLevel > 0) {
                    setCurrentScale(newState.currentZoomLevel);

                    if (!isInitialZoomSetRef.current) {
                        isInitialZoomSetRef.current = true;

                        const zoomPercent = Math.round(newState.currentZoomLevel * 100);
                        onPageLoad?.(zoomPercent);

                        setIsViewerReady(true);
                    }
                }
            });

            if (zoomUnsubscribeRef.current) {
                zoomUnsubscribeRef.current();
            }
            zoomUnsubscribeRef.current = zoomUnsubscribe;
        } else {
            setIsViewerReady(true);
        }

        const commandsPlugin = registry.getCapabilityProvider('commands');
        const commandsCapability = commandsPlugin?.provides?.() as CommandsCapability;

        if (commandsCapability) {
            // Custom Highlight Command
            commandsCapability.registerCommand({
                id: 'custom.highlight',
                label: 'ハイライト',
                icon: 'custom-highlight',
                action: async () => {
                    const selectionPlugin = registry.getCapabilityProvider('selection');
                    const selectionCapability = selectionPlugin?.provides?.() as SelectionCapability;
                    if (!selectionCapability) return;

                    try {
                        const selectedText = await getCleanSelectedText(selectionCapability, registry, docManager);

                        if (selectedText) {
                            const selections = selectionCapability.getFormattedSelection();
                            const position = selections.length > 0 ? JSON.stringify(selections[0]) : '';
                            await readerStore.addNote(selectedText, '', position);
                            selectionCapability.clear();
                        }
                    } catch (err) {
                        console.error('Error handling highlight selection:', err);
                    }
                }
            });

            // Custom Note Command
            commandsCapability.registerCommand({
                id: 'custom.note',
                label: 'メモ',
                icon: 'custom-note',
                action: async () => {
                    const selectionPlugin = registry.getCapabilityProvider('selection');
                    const selectionCapability = selectionPlugin?.provides?.() as SelectionCapability;
                    if (!selectionCapability) return;

                    try {
                        const selectedText = await getCleanSelectedText(selectionCapability, registry, docManager);

                        if (selectedText) {
                            const selections = selectionCapability.getFormattedSelection();
                            const position = selections.length > 0 ? JSON.stringify(selections[0]) : '';
                            readerStore.openNoteModal(selectedText, position);
                        }
                    } catch (err) {
                        console.error('Error handling note selection:', err);
                    }
                }
            });
        }

        // Configure Selection Menu
        const uiPlugin = registry.getCapabilityProvider('ui');
        const uiCapability = uiPlugin?.provides?.() as UICapability;

        if (uiCapability) {
            // Hide main toolbar
            uiCapability.mergeSchema({
                toolbars: {
                    'main-toolbar': {
                        id: 'main-toolbar',
                        position: { placement: 'top', slot: 'main' },
                        items: [],
                        categories: ['toolbar']
                    }
                }
            });

            let items: any[] = [];

            if (!activeBook?.publication?.isPreview) {
                items.push({ type: 'command-button', id: 'custom-highlight', commandId: 'custom.highlight', variant: 'icon', categories: ['selection'] });
                items.push({ type: 'command-button', id: 'custom-note', commandId: 'custom.note', variant: 'icon', categories: ['selection'] });
            }

            // Configure Selection Menu
            const menuId = 'selection';
            uiCapability.mergeSchema({
                selectionMenus: {
                    [menuId]: {
                        id: menuId,
                        items: items
                    }
                }
            });
        }

        // Apply initial highlights
        setTimeout(() => {
            applyNoteHighlights();
            applySearchHighlights();
        }, 500);
    }, [applyNoteHighlights, applySearchHighlights, readerStore, activeBook]);

    const handlePrevPage = () => {
        if (activeBook && activeBook.currentPage > 1) {
            readerStore.fetchPage(activeBook.publication.id, activeBook.currentPage - 1);
        }
    };

    const handleNextPage = () => {
        const totalPages = activeBook?.publication.isPreview ?
            activeBook.publication.previewPageCount :
            activeBook?.publication.totalNumberPages;

        if (activeBook && totalPages && activeBook.currentPage < totalPages) {
            readerStore.fetchPage(activeBook.publication.id, activeBook.currentPage + 1);
        }
    };

    const totalPages = activeBook?.publication.isPreview ?
        Math.min(activeBook.publication.previewPageCount, activeBook.publication.totalNumberPages) :
        activeBook?.publication.totalNumberPages;

    const canGoPrev = activeBook && activeBook.currentPage > 1;
    const canGoNext = activeBook && totalPages && activeBook.currentPage < totalPages;

    if (!activeBook) {
        return null;
    }

    return (
        <div className='flex-1 flex items-center justify-center relative overflow-hidden bg-[#F5F5F7]'>
            <button
                onClick={handlePrevPage}
                disabled={!canGoPrev}
                className={`absolute left-2 sm:left-4 z-10 p-2 sm:p-3 rounded-full bg-[#29292B] transition-all duration-200 shadow-lg hidden sm:block ${canGoPrev ? 'text-white hover:bg-[#1a1a1a] cursor-pointer' : 'text-gray-600 cursor-not-allowed opacity-50'}`}
                title='前のページ'
            >
                <ChevronLeft className='w-5 h-5 sm:w-6 sm:h-6' />
            </button>

            <div className='w-full h-full flex items-center justify-center'>
                <div className='w-full h-full relative flex items-center justify-center'>
                    {
                        currentPageUrl ?
                            (
                                documentError ?
                                    (
                                        <div className='flex flex-col items-center justify-center text-center text-gray-400'>
                                            <p className='text-lg'>ページを表示できません</p>
                                            <p className='text-sm mt-2'>データの問題によりページを表示できません。ページを再読み込みしてください。</p>
                                        </div>
                                    ) :
                                    (
                                        <div
                                            ref={pageContainerRef}
                                            className='w-full h-full flex justify-center relative overflow-visible'
                                        >
                                            <PDFViewer
                                                ref={viewerRef}
                                                key={viewerKey}
                                                style={{ flex: 1, visibility: isViewerReady ? 'visible' : 'hidden' }}
                                                config={{
                                                    src: currentPageUrl,
                                                    theme: {
                                                        preference: 'light',
                                                        light: {
                                                            accent: {
                                                                primary: '#016743',
                                                                primaryHover: '#016743',
                                                                primaryActive: '#016743',
                                                                primaryLight: '#fff',
                                                                primaryForeground: '#fff'
                                                            }
                                                        }
                                                    },
                                                    disabledCategories: [
                                                        'zoom',
                                                        'mode',
                                                        'document',
                                                        'page',
                                                        'panel',
                                                        'history',
                                                        'tools',
                                                        'toolbar'
                                                    ],
                                                    zoom: {
                                                        defaultZoomLevel: ZoomMode.FitPage
                                                    },
                                                    pan: {
                                                        defaultMode: 'never'
                                                    },
                                                    i18n: {
                                                        defaultLocale: 'ja',
                                                        locales: [jaJP]
                                                    }
                                                }}
                                                onReady={handleViewerReady}
                                            />
                                            {(isLoadingPage || !isViewerReady) && (
                                                <div className='absolute inset-0 flex flex-col items-center bg-[#f3f4f6]/40 backdrop-blur-xs justify-center z-20'>
                                                    <Loader2 className='w-12 h-12 text-primary animate-spin mb-4' />
                                                </div>
                                            )}
                                        </div>
                                    )
                            ) :
                            (
                                !isLoadingPage && (
                                    <div className='flex flex-col items-center justify-center text-gray-400'>
                                        <p className='text-lg'>ページはまだ準備できていません</p>
                                        <p className='text-sm mt-2'>現在このページのデータを取得できません。再読み込みしてみてください。</p>
                                    </div>
                                )
                            )
                    }
                </div>
            </div>

            <button
                onClick={handleNextPage}
                disabled={!canGoNext}
                className={`absolute right-2 sm:right-4 z-10 p-2 sm:p-3 rounded-full bg-[#29292B] transition-all duration-200 shadow-lg hidden sm:block ${canGoNext ? 'text-white hover:bg-[#1a1a1a] cursor-pointer' : 'text-gray-600 cursor-not-allowed opacity-50'}`}
                title='次のページ'
            >
                <ChevronRight className='w-5 h-5 sm:w-6 sm:h-6' />
            </button>
        </div>
    );
});

export default PDFPageViewer;
